/**
 * Contrato con el canal público del API (`/widget/{publicId}/…`).
 *
 * Las peticiones van en PascalCase y las respuestas llegan en camelCase, que es la convención
 * que ya usa el resto del frontend contra este backend.
 */

/** Cuerpo de `POST /widget/{publicId}/session`. */
export interface SessionRequest {
  /**
   * Identidad del visitante si ya se le conocía. Ausente en la primera visita.
   *
   * No autoriza nada: solo permite reanudar su conversación. Lo que autoriza los turnos es el token
   * de sesión que devuelve esta llamada.
   */
  VisitorToken?: string | undefined;
}

/** Respuesta del arranque: todo lo que el panel necesita para pintarse y para conversar. */
export interface SessionResponse {
  /** Credencial de vida corta que autoriza los turnos. */
  sessionToken: string;

  /**
   * Identidad del visitante, emitida si no la traía o rotada si el servidor lo decide. La guarda el
   * loader, que es el único con almacenamiento de primera parte.
   */
  visitorToken: string;

  /**
   * Base del streaming. Vacía o ausente significa «usa la del API».
   *
   * Llega del servidor porque es una decisión de despliegue: API Gateway no transmite la respuesta
   * por fragmentos y hay que salir por la URL de función de la Lambda.
   */
  streamBase?: string | null;

  /** Cómo se presenta el agente. Nada de identificadores internos (SEC-11). */
  presentation: SessionPresentation;

  limits: SessionLimits;

  /** Conversación que se reanuda, si el visitante ya tenía una. */
  conversation?: SessionConversation | null;
}

export interface SessionPresentation {
  /** Título de la cabecera: el que fija la publicación o, sin él, el nombre del agente. */
  name: string;

  welcomeMessage?: string | null;

  /** Color de marca en `#RRGGBB`. */
  primaryColor?: string | null;

  avatarUrl?: string | null;

  /** Línea bajo el título. Ausente en un API anterior a la release 006. */
  subtitle?: string | null;

  /** Tema de color: `Light`, `Dark` o `Auto` (el del sistema). Ausente: claro. */
  theme?: string | null;

  /** Se muestra la cabecera. Solo el chat incrustado puede ocultarla. Ausente: sí. */
  showHeader?: boolean | null;

  /** Texto del campo de escritura. Ausente: el de siempre. */
  inputPlaceholder?: string | null;
}

/** Cuerpo de `POST /widget/{publicId}/embed-session`: la apertura del chat incrustado. */
export interface EmbedSessionRequest {
  VisitorToken?: string | undefined;

  /** Origen de la web que contiene el iframe, leído del navegador. */
  EmbedderOrigin: string;
}

export interface SessionLimits {
  /** Longitud máxima de un mensaje del visitante (SEC-22). */
  maxMessageLength: number;
}

export interface SessionConversation {
  id: string;

  /** Últimos mensajes del hilo, en orden. Acotados por el servidor (PERF-11). */
  messages: SessionMessage[];
}

export interface SessionMessage {
  /**
   * Quién dijo el mensaje. Llega como el nombre del enumerado del backend (`User`, `Assistant`), no
   * en minúsculas; se compara con `isUserMessage` para no depender de eso en cada sitio.
   */
  role: string;

  text: string;

  /**
   * Componentes que acompañaban a la respuesta, persistidos con el mensaje. No se pueden
   * recalcular: dependen de lo que devolvió un sistema de terceros en su momento.
   */
  uiParts?: McpUiPart[];
}

/** Indica si el mensaje lo escribió el visitante. */
export function isUserMessage(message: SessionMessage): boolean {
  return message.role.toLowerCase() === 'user';
}

/** Cuerpo de `POST /widget/{publicId}/ask`. */
export interface AskRequest {
  Question: string;
  ConversationId?: string | undefined;
}

/** Fragmento de una respuesta en streaming. */
export interface AskStreamChunk {
  delta?: string;
  isFinal?: boolean;
  noAnswer?: boolean;
  conversationId?: string;

  /**
   * Componentes con los que enriquecer la respuesta. Significan dos cosas según el fragmento: en
   * uno intermedio son las fichas que coloca ese mismo texto y se acumulan; en el final son la
   * lista completa y mandan sobre lo acumulado.
   */
  uiParts?: McpUiPart[];
}

/*
 * Componentes de interfaz aportados por las herramientas MCP del agente.
 *
 * El servidor MCP elige la SEMÁNTICA («esto es una lista», «esto es bueno») y el panel elige el
 * ASPECTO: un servidor manda tokens, nunca colores. Importa especialmente aquí, porque el widget se
 * pinta dentro de la web del cliente y con su color de marca.
 *
 * El canal público no entrega el nombre de la herramienta que produjo cada componente: es un detalle
 * interno de la plataforma.
 */

/** Tipos que el panel sabe pintar. Vocabulario cerrado, igual que en el backend. */
export type McpUiPartType = 'List' | 'Detail' | 'Cards';

/** Intención de un valor, no su color. Un token desconocido cae en `neutral`. */
export type McpUiTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** Peso visual. Un valor desconocido cae en `normal`. */
export type McpUiEmphasis = 'normal' | 'strong' | 'muted';

export interface McpUiBadge {
  text?: string;
  tone?: McpUiTone;
}

/** Dato suelto de una tarjeta: una etiqueta corta y su valor. */
export interface McpUiMetaField {
  label?: string;
  value?: string;
}

/**
 * Vocabulario de una ficha: lo que se puede contar de una cosa concreta.
 *
 * Lo comparten un elemento de `Cards` y una ficha suelta de `Detail`, que son la misma información
 * con distinto tamaño: así un servidor no tiene que saber en cuál va a caer antes de rellenarlo.
 */
export interface McpUiCardContent {
  subtitle?: string;
  description?: string;
  badge?: McpUiBadge;
  emphasis?: McpUiEmphasis;

  /** Enlace al sistema de origen. Solo se admite http y https. */
  url?: string;

  /** Imagen. Solo http y https. */
  imageUrl?: string;

  /**
   * Valor destacado: lo que se lee primero. Genérico a propósito, no `price`: en un inmueble
   * destaca el precio y en un pedido el estado, y un nombre de dominio en el protocolo obligaría a
   * un tipo de ficha por sector.
   */
  highlight?: string;

  /** Datos sueltos que acompañan al destacado. */
  meta?: McpUiMetaField[];

  /**
   * Características sueltas, sin valor asociado. Solo lo pinta `Detail`.
   *
   * Son presencias y no datos —«Piscina» está o no está—, así que meterlas en `meta` obligaría a
   * inventarles un valor.
   */
  features?: string[];
}

/**
 * Elemento de una colección, sea lista o tarjetas. Un solo tipo para los dos: `List` ignora los
 * campos que solo pinta `Cards`.
 */
export interface McpUiListItem extends McpUiCardContent {
  title?: string;
}

export interface McpUiDetailField {
  label?: string;
  value?: string;
  tone?: McpUiTone;
}

/**
 * Contenido de un componente, tal como lo declaró el servidor.
 *
 * Todo opcional salvo el tipo: el panel tolera lo que falte, para que un servidor que evolucione su
 * salida no rompa el chat de un cliente.
 */
export interface McpUiPayload extends McpUiCardContent {
  type?: string;
  title?: string;

  /** Elementos de una colección: `List` y `Cards`. */
  items?: McpUiListItem[];

  /**
   * Pares de campo y valor de una ficha. `Detail`.
   *
   * Conviven con el vocabulario de {@link McpUiCardContent}: un servidor puede mandar los dos y se
   * pintan los dos.
   */
  fields?: McpUiDetailField[];
}

export interface McpUiPart {
  type?: string;
  payload?: McpUiPayload;

  /**
   * Número con el que el texto la referencia. Con número, en el texto hay una marca
   * `[[atlas-ui:n]]` y la ficha se dibuja ahí; sin número va al final del mensaje.
   */
  number?: number;
}

/** Motivo por el que una llamada al canal del widget no se ha podido atender. */
export type WidgetErrorKind =
  /** Publicación inexistente, desactivada, agente sin publicar u origen no autorizado. */
  | 'unavailable'
  /** La sesión ha caducado y hay que volver a arrancar. */
  | 'expired'
  /** Se ha superado un límite de uso. */
  | 'throttled'
  /** El servidor ha rechazado el mensaje: no se reintenta solo. */
  | 'rejected'
  /** Fallo de red o del servidor. */
  | 'network';

export class WidgetError extends Error {
  readonly kind: WidgetErrorKind;

  constructor(kind: WidgetErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = 'WidgetError';
  }
}
